import React, { useRef, useState } from "react";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/pro-regular-svg-icons";
import "./style.scss";

export interface IInputProps {
  className?: string;
  placeholder?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  value?: string;
  disabled?: boolean;
  type?: "text" | "password" | "email" | "search";
  prefixCls?: string;
  id?: string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  name?: string;
  style?: React.CSSProperties;
}

const Input: React.FC<IInputProps> = props => {
  const {
    prefixCls = "rc-input",
    type = "text",
    className,
    onChange,
    onKeyDown,
    prefix = null,
    suffix = null,
    ...rest
  } = props;

  const [value, setValue] = useState<string>("");
  const [focused, setFocused] = useState<boolean>(false);
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);

  const showPasswordToggle = type === "password";
  const inputDomType = showPasswordToggle ? (passwordVisible ? "text" : "password") : type;
  const useAffix = Boolean(prefix || suffix);
  const usePasswordOverlay = showPasswordToggle && !useAffix;

  function resolveOnChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (!onChange) {
      return;
    }
    onChange(e);
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    resolveOnChange(e);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!onKeyDown) {
      return;
    }

    onKeyDown(event);
  };

  const inputClassName = classNames(prefixCls, {
    [`${prefixCls}-${type}`]: type,
    [`${className}`]: !useAffix && !usePasswordOverlay,
  });

  const passwordInputClassName = classNames(prefixCls, {
    [`${prefixCls}-${type}`]: type,
  });

  const prefixNode = prefix ? <span className={`${prefixCls}-prefix`}>{prefix}</span> : null;
  const suffixNode = suffix ? <span className={`${prefixCls}-suffix`}>{suffix}</span> : null;

  const inputRef = useRef<HTMLInputElement | null>(null);

  const renderPasswordToggle = (overlay: boolean) => {
    if (!showPasswordToggle) {
      return null;
    }
    return (
      <button
        type="button"
        className={classNames(`${prefixCls}-password-toggle`, {
          [`${prefixCls}-password-toggle--overlay`]: overlay,
        })}
        aria-label={passwordVisible ? "Hide password" : "Show password"}
        aria-pressed={passwordVisible}
        onMouseDown={e => e.preventDefault()}
        onClick={() => setPasswordVisible(v => !v)}
      >
        <FontAwesomeIcon icon={passwordVisible ? faEyeSlash : faEye} />
      </button>
    );
  };

  const renderInput = () => {
    if (usePasswordOverlay) {
      const wrapClassName = classNames(`${prefixCls}-password-wrap`, className);
      return (
        <span className={wrapClassName}>
          <input
            ref={inputRef}
            type={inputDomType}
            value={value}
            onChange={e => handleChange(e)}
            onKeyDown={handleKeyDown}
            {...rest}
            className={passwordInputClassName}
          />
          {renderPasswordToggle(true)}
        </span>
      );
    }

    const affixWrapperClass = classNames(`${prefixCls}-affix-wrapper`, {
      [`${prefixCls}-affix-wrapper-focused`]: focused,
      [`${className}`]: useAffix,
    });

    const focusInput = () => {
      inputRef.current?.focus();
      setFocused(true);
    };

    const onBlur = () => {
      setFocused(false);
    };

    if (useAffix) {
      return (
        <span className={affixWrapperClass} onClick={focusInput}>
          {prefixNode}
          <input
            ref={inputRef}
            onBlur={onBlur}
            onFocus={focusInput}
            type={inputDomType}
            value={value}
            onChange={e => handleChange(e)}
            onKeyDown={handleKeyDown}
            {...rest}
            className={inputClassName}
          />
          {suffixNode}
          {renderPasswordToggle(false)}
        </span>
      );
    }

    return (
      <input
        type={inputDomType}
        value={value}
        onChange={e => handleChange(e)}
        onKeyDown={handleKeyDown}
        {...rest}
        className={inputClassName}
      />
    );
  };

  return renderInput();
};

export default Input;
